/**
 * Shared running header/footer chrome used by all interior pages of the
 * reviewer report.
 *
 * Header (top):
 *   Line 1:  <Client> · Cybersecurity Risk Assessment Report
 *   Line 2:  <Framework / Framework / Framework>   (smaller, muted)
 *   Gold hairline at the base.
 *
 * Footer (bottom):
 *   "Confidential · For intended recipient use only"      "Page N"
 *
 * Section title helper (drawSectionTitle):
 *   Single-line "SECTION 0X: <Title>" — gold prefix + navy title.
 *   When `continued = true`, renders "SECTION 0X (cont'd): <Title>".
 */
import type jsPDF from "jspdf";
import { PAGE_MARGIN, REPORT_THEME } from "./theme";

export interface HeaderArgs {
  clientName: string;
  /** Selected methodology frameworks (canonical labels). */
  frameworks?: string[] | null;
  /** Legacy single-string methodology, used only when frameworks is empty. */
  methodology?: string | null;
}

const HEADER_STRIP_H = 44;

export function drawRunningHeader(doc: jsPDF, args: HeaderArgs): void {
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFillColor(...REPORT_THEME.accent.rgb);
  doc.rect(0, 0, pageW, HEADER_STRIP_H, "F");

  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(1.2);
  doc.line(0, HEADER_STRIP_H + 1, pageW, HEADER_STRIP_H + 1);

  const baselineY = 26;

  // Right: frameworks (or legacy methodology). Right-aligned, muted on navy.
  const fwList = (args.frameworks ?? []).map((f) => (f || "").trim()).filter(Boolean);
  const rightText =
    fwList.length > 0 ? fwList.join("  /  ") : (args.methodology ?? "").trim();

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(220, 230, 245);
  let rightW = 0;
  if (rightText) {
    rightW = doc.getTextWidth(rightText);
    doc.text(rightText, pageW - PAGE_MARGIN - rightW, baselineY);
  }

  // Left: <Org> · Cybersecurity Risk Assessment Report — clamp to avoid collision.
  doc.setTextColor(...REPORT_THEME.textOnNavy.rgb);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  const leftFull = `${args.clientName}  ·  Cybersecurity Risk Assessment Report`;
  const leftBudget = pageW - PAGE_MARGIN * 2 - (rightW > 0 ? rightW + 18 : 0);
  let leftText = leftFull;
  if (doc.getTextWidth(leftText) > leftBudget) {
    while (leftText.length > 8 && doc.getTextWidth(leftText + "…") > leftBudget) {
      leftText = leftText.slice(0, -1);
    }
    leftText = leftText + "…";
  }
  doc.text(leftText, PAGE_MARGIN, baselineY);
}

export function drawPageFooter(doc: jsPDF, pageNumber: number): void {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const y = pageH - 28;

  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Confidential  ·  For intended recipient use only", PAGE_MARGIN, y);
  const right = `Page ${pageNumber}`;
  doc.text(right, pageW - PAGE_MARGIN - doc.getTextWidth(right), y);
}

/**
 * Standardised section header — single-line layout:
 *   SECTION 05: Assessment Results by Cybersecurity Domain
 *   SECTION 05 (cont'd): Assessment Results by Cybersecurity Domain
 *
 * The "SECTION 0X[ (cont'd)]:" prefix is small + gold; the title is the
 * dominant navy element on the same baseline. A short gold underline
 * accent sits beneath the title.
 *
 * Returns the y-cursor after the underline so callers continue layout
 * below.
 */
export function drawSectionTitle(
  doc: jsPDF,
  args: {
    sectionNumber: string;
    title: string;
    continued?: boolean;
    y?: number;
  },
): number {
  const baseY = args.y ?? 86;
  const prefix = `SECTION ${args.sectionNumber}${args.continued ? " (cont'd)" : ""}: `;

  // Measure the prefix at its own (smaller) size so we can place the
  // title on the same baseline immediately after.
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...REPORT_THEME.gold.rgb);
  doc.text(prefix, PAGE_MARGIN, baseY);
  const prefixW = doc.getTextWidth(prefix);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.setTextColor(...REPORT_THEME.navy.rgb);
  doc.text(args.title, PAGE_MARGIN + prefixW, baseY);

  // Short gold underline beneath the title for consistency with prior
  // section headers.
  doc.setDrawColor(...REPORT_THEME.gold.rgb);
  doc.setLineWidth(1.4);
  doc.line(PAGE_MARGIN, baseY + 8, PAGE_MARGIN + 64, baseY + 8);

  return baseY + 26;
}

/** Render text with manual additional inter-character spacing. */
function drawTrackedText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  extraSpacing: number,
): void {
  let cx = x;
  for (const ch of text) {
    doc.text(ch, cx, y);
    cx += doc.getTextWidth(ch) + extraSpacing;
  }
}

